import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Eye,
  EyeOff,
  Loader2,
  RefreshCw,
  KeyRound,
  Unplug,
  Save,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";

const API_BASE = "https://apisellhub.com/api";

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

// ---------- Section card ----------
const SectionCard = ({
  icon: Icon,
  title,
  description,
  connected,
  children,
}) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
    <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 bg-blue-50">
          <Icon className="h-5 w-5 text-blue-600" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold text-gray-900">{title}</h3>
            {connected ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-50 text-green-700 border border-green-200">
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 border border-gray-200">
                Not connected
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ---------- API key field with show/hide toggle ----------
const SecretField = ({ label, value, onChange, placeholder }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-3 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-700"
          tabIndex={-1}
        >
          {visible ? (
            <EyeOff className="w-4 h-4" />
          ) : (
            <Eye className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
};

// ================ MAIN PAGE ================
const PaymentSettings = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyKey, setBusyKey] = useState(null); // which action is currently running

  // Sellhub
  const [sellhubApiKey, setSellhubApiKey] = useState("");
  const [sellhubConnected, setSellhubConnected] = useState(false);

  // Merge whatever shape the backend returns into local state
  const applyPayload = (payload) => {
    const data = payload?.data ?? payload ?? {};

    // Sellhub — backend returns `sellhub_settings` (plural)
    const sellhub =
      data.sellhub_settings ??
      data.sellhub_setting ??
      data.sellhub ??
      data.sellhubSettings ??
      data.sellhubSetting ??
      null;
    if (sellhub) {
      setSellhubApiKey(sellhub.api_key ?? "");
      setSellhubConnected(Boolean(sellhub.api_key));
    } else {
      setSellhubApiKey("");
      setSellhubConnected(false);
    }
  };

  const fetchSettings = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      const token = getToken();
      if (!token) {
        setLoading(false);
        setRefreshing(false);
        return;
      }
      const res = await axios.get(`${API_BASE}/super-admin/payment-settings`, {
        headers: authHeaders(token),
      });
      applyPayload(res.data);
    } catch (err) {
      console.error("Payment settings fetch error:", err);
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("Market-token");
        navigate("/center/login", { replace: true });
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to load payment settings");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Save action ----
  const saveSellhub = async () => {
    if (!sellhubApiKey.trim()) {
      toast.error("Sellhub API key is required");
      return;
    }
    try {
      setBusyKey("sellhub-save");
      const token = getToken();
      await axios.put(
        `${API_BASE}/super-admin/sellhub-setting`,
        { api_key: sellhubApiKey.trim() },
        { headers: authHeaders(token) }
      );
      toast.success("Sellhub gateway updated");
      fetchSettings(true);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "Failed to save Sellhub");
    } finally {
      setBusyKey(null);
    }
  };

  // ---- Disconnect action ----
  const disconnectSellhub = async () => {
    if (
      !window.confirm(
        "Disconnect Sellhub? This removes the saved configuration."
      )
    )
      return;
    try {
      setBusyKey("sellhub-disconnect");
      const token = getToken();
      await axios.delete(
        `${API_BASE}/super-admin/sellhub-setting-disconnect`,
        { headers: authHeaders(token) }
      );
      toast.success("Sellhub disconnected");
      fetchSettings(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? "Failed to disconnect Sellhub"
      );
    } finally {
      setBusyKey(null);
    }
  };

  const anyBusy = (...keys) => keys.some((k) => busyKey === k);

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Settings</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage the Sellhub gateway credentials used for payments across the
            platform.
          </p>
        </div>
        <button
          onClick={() => fetchSettings(true)}
          disabled={loading || refreshing}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="ml-3 text-sm text-gray-600">
            Loading payment settings…
          </span>
        </div>
      ) : (
        <div className="space-y-5">
          {/* ========== SELLHUB ========== */}
          <SectionCard
            icon={KeyRound}
            title="Sellhub Gateway"
            description="Connect your Sellhub API key to accept payments through the Sellhub processor"
            connected={sellhubConnected}
          >
            <SecretField
              label="API Key"
              value={sellhubApiKey}
              onChange={setSellhubApiKey}
              placeholder="sellhub-api-key"
            />
            <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
              {sellhubConnected && (
                <button
                  onClick={disconnectSellhub}
                  disabled={anyBusy("sellhub-disconnect", "sellhub-save")}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-red-600 border border-red-200 bg-white hover:bg-red-50 disabled:opacity-50"
                >
                  {busyKey === "sellhub-disconnect" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Unplug className="w-4 h-4" />
                  )}
                  Disconnect
                </button>
              )}
              <button
                onClick={saveSellhub}
                disabled={anyBusy("sellhub-save", "sellhub-disconnect")}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {busyKey === "sellhub-save" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {sellhubConnected ? "Update" : "Connect"}
              </button>
            </div>
          </SectionCard>
        </div>
      )}
    </div>
  );
};

export default PaymentSettings;
